import type { AriaRole, ButtonHTMLAttributes, HTMLAttributes } from "react";

import type { TIconKey } from "@/ui/components/primitives/Icon";
import type { DefinedDefaults, TWithDataComponent } from "@/ui/lib/types";

/*-------------------------------------
|  Constants
|-------------------------------------*/

export const SONNER_ROOT_CLASS = "flex w-[21rem] max-w-full items-start gap-2 rounded-sys-xs border border-solid border-outline-dark bg-surface-default p-4 text-foreground shadow-md outline-none";

/** Focus glow from the Figma "Focus" state: a 2px ring plus a deeper elevation shadow. */
export const SONNER_ROOT_FOCUS_CLASS = "focus-visible:shadow-[0px_0px_0px_2px_rgba(0,0,0,0.2),0px_4px_12px_0px_rgba(0,0,0,0.1)]";

export const SONNER_CONTENT_CLASS = "flex min-w-0 flex-1 flex-col gap-2";

export const SONNER_ICON_CLASS = "size-6 shrink-0 text-foreground";

export const SONNER_ICON_LOADING_CLASS = "animate-spin text-foreground-muted";

export const SONNER_DESCRIPTION_CLASS = "text-foreground-muted-strong";

export const SONNER_ACTION_CLASS = "shrink-0 self-start";

export const SONNER_CLOSE_CLASS = "inline-flex size-6 shrink-0 items-center justify-center rounded-sys-xs text-foreground transition-colors hover:bg-surface-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:pointer-events-none disabled:text-foreground-muted";

/** Icon shown in place of the leading icon while the toast is loading. */
export const SONNER_LOADING_ICON = "loader" satisfies TIconKey;

/*-------------------------------------
|  Types
|-------------------------------------*/

export type TSonnerAction = {
  label: string;
  onClick: () => void;
};

export type TSonnerProps = {
  /** Bold heading rendered with the `Label` component. */
  title: string;
  /** Primary supporting copy rendered with `TextBody`. */
  description?: string;
  /** Secondary muted line (e.g. a timestamp) rendered beneath the description. */
  detail?: string;
  /** Leading icon; pass `null` to hide it. Ignored while `loading`. */
  icon?: TIconKey | null;
  /** Replaces the leading icon with an animated spinner and sets `aria-busy`. */
  loading?: boolean;
  /** Optional trailing action rendered as a tertiary `Button`. */
  action?: TSonnerAction;
  /** When provided, renders a trailing close button that calls this handler. */
  onDismiss?: () => void;
  /** Accessible label for the close button. */
  dismissLabel?: string;
} & TWithDataComponent & Omit<HTMLAttributes<HTMLDivElement>, "title">;

export type TSonnerCloseProps = ButtonHTMLAttributes<HTMLButtonElement>;

type TSonnerDefaultProps = DefinedDefaults<
  TSonnerProps,
  "icon" |
  "loading" |
  "dismissLabel"
> & { role: AriaRole; tabIndex: number };

/*-------------------------------------
|  Defaults
|-------------------------------------*/

export const SONNER_DEFAULTS = {
  icon: "circleTick",
  loading: false,
  dismissLabel: "Dismiss notification",
  role: "status",
  tabIndex: 0,
} satisfies TSonnerDefaultProps;
